import { useEffect, useRef } from 'react';
import type { NodeKind } from '../types';

export interface ContextMenuPosition {
  x: number;        // Screen coords
  y: number;
  flowX: number;    // React Flow canvas coords
  flowY: number;
}

interface Props {
  open: boolean;
  pos: ContextMenuPosition | null;
  onClose: () => void;
  onAddNode: (kind: NodeKind, flowX: number, flowY: number) => void;
  onBuildWithAI: () => void;
  onClear: () => void;
  onPaste: () => void;
  hasClipboard: boolean;
}

const NODE_OPTIONS: { kind: NodeKind; label: string; sub: string; icon: string; cls: string }[] = [
  { kind: 'trigger',   label: 'When this happens',  sub: 'Start the workflow',  icon: '⚡', cls: 'trig' },
  { kind: 'wait',      label: 'Wait a bit',         sub: 'Pause before next',   icon: '⏱', cls: 'wait' },
  { kind: 'condition', label: 'Only if',            sub: 'Filter / branch',      icon: '◇', cls: 'cond' },
  { kind: 'action',    label: 'Then do this',       sub: 'SMS, email, task…',    icon: '▶', cls: 'act' },
  { kind: 'ai_smart',  label: 'Ask AI',             sub: 'Custom prompt + model', icon: '★', cls: 'ai' },
];

export function ContextMenu({ open, pos, onClose, onAddNode, onBuildWithAI, onClear, onPaste, hasClipboard }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open || !pos) return null;

  // Clamp so menu stays on screen
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const MENU_W = 260;
  const MENU_H = 410;
  const left = Math.min(pos.x, vw - MENU_W - 12);
  const top = Math.min(pos.y, vh - MENU_H - 12);

  return (
    <div ref={ref} className="ctx-menu" style={{ left, top }} role="menu">
      <button className="ctx-item primary" onClick={() => { onBuildWithAI(); onClose(); }}>
        <span className="ctx-ic gold">✨</span>
        <div className="ctx-text">
          <b>Build with AI</b>
          <span>Describe it · I'll build it</span>
        </div>
      </button>

      <div className="ctx-divider">Add a block here</div>

      {NODE_OPTIONS.map(opt => (
        <button
          key={opt.kind}
          className={`ctx-item ${opt.cls}`}
          onClick={() => { onAddNode(opt.kind, pos.flowX, pos.flowY); onClose(); }}
        >
          <span className={`ctx-ic ${opt.cls}`}>{opt.icon}</span>
          <div className="ctx-text">
            <b>{opt.label}</b>
            <span>{opt.sub}</span>
          </div>
        </button>
      ))}

      <div className="ctx-divider">Other</div>

      <button className="ctx-item" onClick={() => { onPaste(); onClose(); }} disabled={!hasClipboard}>
        <span className="ctx-ic">⎘</span>
        <div className="ctx-text">
          <b>Paste node</b>
          <span>{hasClipboard ? 'From last copy' : 'Nothing to paste yet'}</span>
        </div>
      </button>

      <button className="ctx-item danger" onClick={() => { onClear(); onClose(); }}>
        <span className="ctx-ic">🗑</span>
        <div className="ctx-text">
          <b>Clear all blocks</b>
          <span>Reset the canvas</span>
        </div>
      </button>
    </div>
  );
}
